"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { Play, Pause } from 'lucide-react';
import { useNavigate } from "react-router-dom";

const videos = [
  {
    id: 'video-1',
    url: "https://d1twcv82c6wl0s.cloudfront.net/video-1.mp4",
    title: "Medieval Battle",
    thumbnail: "https://d1twcv82c6wl0s.cloudfront.net/image-1.png",
  },
  {
    id: 'video-2',
    url: "https://d1twcv82c6wl0s.cloudfront.net/video-2.mp4",
    title: "Family Man",
    thumbnail: "https://d1twcv82c6wl0s.cloudfront.net/image-2.png",
  },
  {
    id: 'video-3',
    url: "https://d1twcv82c6wl0s.cloudfront.net/video-3.mp4",
    title: "Robots vs Robots",
    thumbnail: "https://d1twcv82c6wl0s.cloudfront.net/image-3.png",
  },
  {
    id: 'video-4',
    url: "https://d1twcv82c6wl0s.cloudfront.net/video-4.mp4",
    title: "Gangsters Busted",
    thumbnail: "https://d1twcv82c6wl0s.cloudfront.net/image-4.png",
  },
  {
    id: 'video-5',
    url: "https://d1twcv82c6wl0s.cloudfront.net/video-5.mp4",
    title: "Space Wars",
    thumbnail: "https://d1twcv82c6wl0s.cloudfront.net/image-5.png",
  },
];

const TimelineMarker = ({ position }) => (
  <div
    className="absolute bottom-[-5px] transform -translate-x-1/2"
    style={{ left: `${position}%` }}
  >
    <img
          src="../assets/images/red-flag.png"
          alt="red-flag"
          className="w-[32px] h-[50px] aspect-video object-cover"
        />
    {/* <svg
      className="w-6 h-6 text-white filter drop-shadow-glow"
      width="22"
      height="27"
      viewBox="0 0 22 27"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M0.386042 17.4634C0.149652 17.2407 0.0110188 16.9329 0.000627518 16.6076C-0.00976372 16.2824 0.10894 15.9662 0.330633 15.7288L9.77776 15.7288L9.77776 1.22728C9.77776 0.901787 9.90653 0.589621 10.1357 0.359461C10.365 0.129301 10.6758 0 11 0C11.3242 0 11.635 0.129301 11.8643 0.359461C12.0935 0.589621 12.2222 0.901787 12.2222 1.22728L12.2222 15.7288L21.6694 15.7288C21.8911 15.9662 22.0098 16.2824 21.9994 16.6076C21.989 16.9329 21.8503 17.2407 21.614 17.4634L11.836 26.668C11.6095 26.8813 11.3106 27 11 27C10.6894 27 10.3905 26.8813 10.164 26.668L0.386042 17.4634Z"
        fill="white"
      />
    </svg> */}
  </div>
);

const VideoProgress = ({
  videoRef,
  isEnabled = false,
  onProgressClick,
  markers = [],
}) => {
  const progressRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const isDragging = useRef(false);

  const updateProgress = useCallback(() => {
    if (!videoRef.current) return;
    const progress =
      (videoRef.current.currentTime / videoRef.current.duration) * 100;
    setProgress(progress);
  }, [videoRef]);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const handleTimeUpdate = () => {
      if (!isDragging.current) {
        updateProgress();
      }
    };

    videoElement.addEventListener("timeupdate", handleTimeUpdate);
    return () =>
      videoElement.removeEventListener("timeupdate", handleTimeUpdate);
  }, [updateProgress]);

  const handleMouseDown = (e) => {
    if (!isEnabled) return;
    isDragging.current = true;
    handleMouseMove(e);
  };

  const handleMouseMove = (e) => {
    if (!isEnabled || !isDragging.current || !progressRef.current) return;
    const rect = progressRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const percentage = (x / rect.width) * 100;

    videoRef.current.currentTime =
      (percentage / 100) * videoRef.current.duration;
    setProgress(percentage);
    onProgressClick?.(percentage);
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  useEffect(() => {
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  return (
    <div
      ref={progressRef}
      className={`relative h-2 bg-white rounded-full ${
        isEnabled ? "cursor-pointer" : ""
      }`}
      onMouseDown={handleMouseDown}
    >
      <div
        className="absolute top-0 left-0 h-full bg-teal-600 rounded-full transition-[width] duration-75"
        style={{ width: `${progress}%` }}
      />
      {markers.map((marker, index) => (
        <TimelineMarker key={index} position={marker.position} />
      ))}
    </div>
  );
};

const VideoThumbnail = ({ video, onClick, isActive }) => {
  return (
    <div onClick={onClick} className="relative cursor-pointer group">
      <div
        className="relative overflow-hidden rounded-lg video-shadow"
        style={{
          border: "2px solid #57ba98",
        }}
      >
        <img
          src={video.thumbnail}
          alt={video.title}
          className="w-full h-full aspect-video object-cover transition-transform duration-300 group-hover:scale-105 bg-gray-900"
        />
        <div
          className={`absolute inset-0 ${
            isActive
              ? "bg-teal-500/40"
              : "bg-teal-500/20 group-hover:bg-teal-500/30"
          } transition-colors`}
        />
        <div className="absolute inset-0 opacity-50 bg-gradient-to-t from-teal-500/20 via-transparent to-transparent" />
      </div>
      <p className="mt-2 text-white text-center">{video.title}</p>
    </div>
  );
};

export default function Home() {
  const navigate = useNavigate();
  const [activeVideo, setActiveVideo] = useState(videos[0]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMarking, setIsMarking] = useState(false);
  const [markers, setMarkers] = useState([]);
  const [tempMarkerPosition, setTempMarkerPosition] = useState(null);
  const mainVideoRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const videoElement = mainVideoRef.current;
    if (videoElement) {
      videoElement.pause();
      videoElement.load();
      setIsPlaying(false);
    }
  }, [activeVideo]);

  useEffect(() => {
    const videoElement = mainVideoRef.current;
    if (!videoElement) return;

    const handleEnded = () => {
      setIsPlaying(false);
    };

    const handleError = (e) => {
      console.error("Video error:", e);
      setIsPlaying(false);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(videoElement.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(videoElement.duration);
    };

    videoElement.addEventListener("ended", handleEnded);
    videoElement.addEventListener("error", handleError);
    videoElement.addEventListener("timeupdate", handleTimeUpdate);
    videoElement.addEventListener("loadedmetadata", handleLoadedMetadata);

    return () => {
      videoElement.removeEventListener("ended", handleEnded);
      videoElement.removeEventListener("error", handleError);
      videoElement.removeEventListener("timeupdate", handleTimeUpdate);
      videoElement.removeEventListener("loadedmetadata", handleLoadedMetadata);
    };
  }, []);

  const handleVideoClick = (video) => {
    if (video.id !== activeVideo.id) {
      setActiveVideo(video);
      setMarkers([]);
      setIsMarking(false);
    }
  };

  const togglePlayPause = async () => {
    const videoElement = mainVideoRef.current;
    if (!videoElement) return;

    try {
      if (isPlaying) {
        videoElement.pause();
        setIsPlaying(false);
      } else {
        await videoElement.play();
        setIsPlaying(true);
      }
    } catch (error) {
      console.error("Error toggling play/pause:", error);
      setIsPlaying(false);
    }
  };

  const handleProgressClick = (percentage) => {
    if (isMarking) {
      setTempMarkerPosition(percentage);
      if (mainVideoRef.current) {
        mainVideoRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  const formatTimeForDisplay = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  const formatTimeForStorage = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    const milliseconds = Math.floor((seconds % 1) * 1000);
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}.${milliseconds.toString().padStart(3, "0")}`;
  };

  const handleAddMarker = () => {
    const videoElement = mainVideoRef.current;
    if (!videoElement) return;

    const currentPosition =
      (videoElement.currentTime / videoElement.duration) * 100;
    const newMarker = {
      id: Date.now(),
      position: currentPosition,
      timestamp: videoElement.currentTime,
      formattedTime: formatTimeForStorage(videoElement.currentTime),
    };
    setMarkers((prev) => [...prev, newMarker]);
    setTempMarkerPosition(null);

    const storedMarkers = JSON.parse(
      localStorage.getItem(`videoMarkers_${activeVideo.id}`) || "[]"
    );
    localStorage.setItem(
      `videoMarkers_${activeVideo.id}`,
      JSON.stringify([...storedMarkers, { time: newMarker.formattedTime }])
    );
  };

  const handleDone = async () => {
    if (loading) return;
    setLoading(true);
    const videoElement = mainVideoRef.current;
    if (!videoElement) return;

    // Store current video state in localStorage first
    localStorage.setItem('currentPlayingVideo', JSON.stringify(activeVideo));
    localStorage.setItem('videoCurrentTime', videoElement.currentTime.toString());

    const endTime = Date.now();
    const totalTime = endTime - startTime;
    const totalMinutes = Math.floor(totalTime / 60000);
    const totalSeconds = Math.floor((totalTime % 60000) / 1000);
    const formattedTime = `${totalMinutes}:${totalSeconds
      .toString()
      .padStart(2, "0")}`;

    const storedUserData = localStorage.getItem("userData");
    let uuvid6;

    if (storedUserData) {
      try {
        const parsedUserData = JSON.parse(storedUserData);
        uuvid6 = parsedUserData.uuvid6;
      } catch (error) {
        console.error("Error parsing userData from localStorage:", error);
        setLoading(false);
        alert("An error occurred while retrieving user information. Please try again.");
        setLoading(false); // Reset loading
        return;
      }
    } else {
      console.error("No userData found in localStorage.");
      setLoading(false);
      alert("User data not found. Please log in again.");
      setLoading(false); // Reset loading
      return;
    }

    const timestamps = markers.reduce((acc, marker, index) => {
      const [minutes, seconds] = marker.formattedTime.split(":");
      const formattedMinutes = parseInt(minutes, 10)
        .toString()
        .padStart(2, "0");
      const formattedSeconds = parseFloat(seconds)
        .toFixed(0)
        .toString()
        .padStart(2, "0");
      acc[index + 1] = `${formattedMinutes}:${formattedSeconds}`;
      return acc;
    }, {});

    const payload = {
      video_id: activeVideo.id,
      user_data: {
        uuvid6,
        timestamps,
        timetake: formattedTime,
      },
    };

    try {
      const response = await fetch(
        "https://29c1ypic2g.execute-api.us-west-1.amazonaws.com/prod1/updatestore",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error(`API request failed: ${response.statusText}`);
      }

      const responseData = await response.json();
      console.log("API Response:", responseData);

      localStorage.setItem("totalResponse", JSON.stringify(responseData));
    } catch (error) {
      console.error("Error:", error);
      localStorage.setItem(
        "totalResponse",
        JSON.stringify({
          accuracy: 0,
          score: 0,
        })
      );
    } finally {
      setLoading(false);
      navigate("/user/done");
    }
  };

  return (
    <main className="min-h-screen w-full relative">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat -z-10"
        style={{
          backgroundImage: "url(../assets/images/user-login-bg.png)",
        }}
      />
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-8 heading-text-user">
          Media Sentinel
        </h1>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          {videos.map((video) => (
            <VideoThumbnail
              key={video.id}
              video={video}
              onClick={() => handleVideoClick(video)}
              isActive={video.id === activeVideo.id}
            />
          ))}
        </div>

        <div className="relative aspect-video w-full rounded-lg overflow-hidden bg-black/80">
          <video
            ref={mainVideoRef}
            className="absolute inset-0 w-full h-full object-cover"
            playsInline
          >
            <source src={activeVideo.url} type="video/mp4" />
            Your browser does not support the video tag.
          </video>

          <button
            onClick={togglePlayPause}
            className={`absolute inset-0 flex items-center justify-center transition-opacity duration-200 ${
              isPlaying
                ? "opacity-0 hover:opacity-100 bg-black/10 hover:bg-black/30"
                : "bg-black/30"
            } group`}
          >
            <div className="rounded-full p-4 bg-teal-600/80 group-hover:bg-teal-600 transition-colors">
              {isPlaying ? (
                <Pause className="w-12 h-12 text-white" />
              ) : (
                <Play className="w-12 h-12 text-white" />
              )}
            </div>
          </button>

          <div className="absolute inset-0 pointer-events-none opacity-50 bg-gradient-to-t from-teal-500/20 via-transparent to-transparent" />
        </div>

        {/* Display current video thumbnail and title */}
        <div className="mt-4 flex items-center gap-4 justify-center">
          <h3 className="text-white text-xl">{activeVideo.title}</h3>
        </div>

        {isMarking && (
          <div className="relative my-12">
            <VideoProgress
              videoRef={mainVideoRef}
              isEnabled={true}
              onProgressClick={handleProgressClick}
              markers={markers}
            />
            <div className="text-white flex justify-between mt-2 text-lg">
              <p>{formatTimeForDisplay(currentTime)} </p>
              <p>{formatTimeForDisplay(duration)}</p>{" "}
            </div>
          </div>
        )}

        <div className="flex gap-4 mt-8">
          {!isMarking ? (
            <>
              <div className="flex justify-between w-full">
                <button
                  onClick={() => {
                    localStorage.clear();
                    navigate("/login");
                  }}
                  className="px-10 py-3 text-white rounded-lg font-medium relative group overflow-hidden button-shadow-user"
                >
                  <span className="relative z-10 text-shadow-user-home">
                    Home
                  </span>
                </button>
                <button
                  onClick={() => {
                    setIsMarking(true);
                    setStartTime(Date.now());
                  }}
                  className="px-10 py-3 text-white rounded-lg font-medium relative group overflow-hidden button-shadow-user"
                >
                  <span className="relative z-10 text-shadow-user-start">
                    Start
                  </span>
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex justify-end w-full gap-4">
                <button
                  onClick={handleAddMarker}
                  className="px-10 py-3 text-white rounded-lg font-medium relative group overflow-hidden button-shadow-user"
                >
                  <span className="relative z-10 text-shadow-user-marker">
                  Flag Scene
                  </span>
                </button>
                <button
                  onClick={handleDone}
                  disabled={loading}
                  className="px-10 py-3 text-white rounded-lg font-medium relative group overflow-hidden button-shadow-user"
                >
                  <span className="relative z-10 text-shadow-user-done">
                    {loading ? "Loading..." : "Done"}
                  </span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
